import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Latex from "react-latex-next";
import { useEffect, useState } from "react";
import {
  anthropomorphicArmLinkList,
  anthropomorphicArmSphericalWristLinkList,
  dlrManipulatorLinkList,
  sphericalArmLinkList,
  sphericalWristLinkList,
  stanfordManipulatorLinkList,
  threeLinkPlanarArmLinkList,
} from "./armTemplate";
import { Vector3 } from "three";
import { linkprops } from "./types";
import * as THREE from "three";
import NextAction from "../../utils/NextAction";

interface ContrlProps {
  linkList: linkprops[];
  setLinkList: React.Dispatch<React.SetStateAction<linkprops[]>>;
  endEffectorPos: THREE.Vector3;
}

export default function Controls({
  linkList,
  setLinkList,
  endEffectorPos,
}: ContrlProps) {
  const [type, setType] = useState("threeLinkPlanarArm");
  const [targetEndEffectorPos, setTargetEndEffectorPos] = useState(
    new Vector3(0, 0, 0)
  );
  const [noSolution, setNoSolution] = useState(true);
  const [results, setResults] = useState([]);
  const [resultIndex, setResultIndex] = useState(0);

  const handleResultIndexChange = (e) => {
    setResultIndex(e.target.value);
    const newLinkList = [...linkList];
    newLinkList[0].theta = results[e.target.value][0];
    newLinkList[1].theta = results[e.target.value][1];
    newLinkList[2].theta = results[e.target.value][2];
    setLinkList(newLinkList);
  };

  const handleChange = (event: SelectChangeEvent) => {
    setType(event.target.value);
    if (event.target.value == "threeLinkPlanarArm") {
      setLinkList(threeLinkPlanarArmLinkList);
    } else if (event.target.value == "sphericalArm") {
      setLinkList(sphericalArmLinkList);
    } else if (event.target.value == "anthropomorphicArm") {
      setLinkList(anthropomorphicArmLinkList);
    } else if (event.target.value == "sphericalWrist") {
      setLinkList(sphericalWristLinkList);
    } else if (event.target.value == "stanfordManipulator") {
      setLinkList(stanfordManipulatorLinkList);
    } else if (event.target.value == "anthropomorphicArmSphericalWrist") {
      setLinkList(anthropomorphicArmSphericalWristLinkList);
    } else if (event.target.value == "dlrManipulator") {
      setLinkList(dlrManipulatorLinkList);
    }
  };

  return (
    <div className="controls">
      <div
        className="controlGroup"
        style={{ display: "flex", flexDirection: "column" }}
      >
        <Typography variant="h5" style={{ paddingBottom: 20 }}>
          Inverse Kinematics
        </Typography>
        <Typography style={{ paddingBottom: 20, textAlign: "left" }}>
          There are two methods of Inverse Kinematics <br />
          1. Analytical solutions <br />
          2.{" "}
          <a target="_blank" href="https://vis-ro.web.app/2d-jacobian-ik">
            Numerical solutions (Jacobian Technique)
          </a>{" "}
          <br />
          In this page, we visualize the analytical solutions.
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          There are different analytical solutions for different robotic arm.
          Please select your robotics arm first.
        </Typography>
        <FormControl variant="standard" sx={{ marginBottom: 2, minWidth: 120 }}>
          <InputLabel id="demo-simple-select-standard-label">
            Robotic Arm Type
          </InputLabel>
          <Select
            labelId="demo-simple-select-standard-label"
            id="demo-simple-select-standard"
            value={type}
            onChange={handleChange}
            label="Age"
          >
            <MenuItem value={"threeLinkPlanarArm"}>
              Three-link Planar Arm
            </MenuItem>
            <MenuItem value={"sphericalArm"}>Spherical Arm</MenuItem>
            <MenuItem value={"anthropomorphicArm"}>
              Anthropomorphic Arm
            </MenuItem>
            <MenuItem value={"sphericalWrist"}>Spherical Wrist</MenuItem>
            <MenuItem value={"stanfordManipulator"}>
              Standard Manipulator
            </MenuItem>
            <MenuItem value={"anthropomorphicArmSphericalWrist"}>
              Anthropomorphic Arm with Spherical Wrist
            </MenuItem>
            <MenuItem value={"dlrManipulator"}>DLR Manipulator</MenuItem>
          </Select>
        </FormControl>
        <TableContainer
          component={Paper}
          sx={{
            maxWidth: 450,
            marginBottom: 1,
            marginRight: "auto",
            marginLeft: "auto",
          }}
        >
          <Table sx={{ minWidth: 350 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell style={{ maxWidth: 30, padding: 10 }} align="center">
                  Link index
                </TableCell>
                <TableCell align="right" style={{ padding: 10 }}>
                  <Latex>$d_n$</Latex>
                </TableCell>
                <TableCell align="right" style={{ padding: 10 }}>
                  <Latex>$\theta_n$</Latex>
                </TableCell>
                <TableCell align="right" style={{ padding: 10 }}>
                  <Latex>$a_n$</Latex>
                </TableCell>
                <TableCell align="right" style={{ padding: 10 }}>
                  <Latex>$\alpha_n$</Latex>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {linkList.map((link, index) => (
                <TableRow
                  key={index}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell
                    component="th"
                    scope="row"
                    align="center"
                    style={{ padding: 10 }}
                  >
                    {index + 1}
                  </TableCell>
                  <TableCell align="right" style={{ padding: 10 }}>
                    {type == "threeLinkPlanarArm" ? (
                      link.d
                    ) : (
                      <input
                        value={link.d}
                        onChange={(e) => {
                          const newLink = { ...link };
                          const value = e.target.value;
                          newLink.d = parseInt(e.target.value);
                          const newLinkList = [...linkList];
                          newLinkList[index] = newLink;
                          setLinkList(newLinkList);
                        }}
                        step={1}
                        type="number"
                      />
                    )}
                  </TableCell>
                  <TableCell align="right" style={{ padding: 10 }}>
                    <input
                      style={{ width: 50 }}
                      value={link.theta.toFixed(2)}
                      onChange={(e) => {
                        const newLink = { ...link };
                        const value = e.target.value;
                        newLink.theta = "" ? 0 : parseInt(e.target.value);
                        const newLinkList = [...linkList];
                        newLinkList[index] = newLink;
                        setLinkList(newLinkList);
                      }}
                      step={1}
                      type="number"
                    />
                  </TableCell>
                  <TableCell align="right" style={{ padding: 10 }}>
                    <input
                      style={{ width: 50 }}
                      value={link.a}
                      onChange={(e) => {
                        const newLink = { ...link };
                        const value = e.target.value;
                        newLink.a = value == "" ? 0 : parseInt(e.target.value);
                        const newLinkList = [...linkList];
                        newLinkList[index] = newLink;
                        setLinkList(newLinkList);
                      }}
                      step={1}
                      type="number"
                    />
                  </TableCell>
                  <TableCell align="right" style={{ padding: 10 }}>
                    {type == "threeLinkPlanarArm" ? (
                      link.alpha
                    ) : (
                      <input
                        value={link.alpha}
                        onChange={(e) => {
                          const newLink = { ...link };
                          const value = e.target.value;
                          newLink.alpha = parseInt(e.target.value);
                          const newLinkList = [...linkList];
                          newLinkList[index] = newLink;
                          setLinkList(newLinkList);
                        }}
                        step={1}
                        type="number"
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography style={{ marginTop: 20, textAlign: "left" }}>
          Current End Effector Position:{" "}
          {`x: ${endEffectorPos.x.toFixed(2)}, y: ${endEffectorPos.y.toFixed(
            2
          )}`}
        </Typography>
        {type == "threeLinkPlanarArm" ? (
          <>
            <Typography
              style={{ marginTop: 20, marginBottom: 20, textAlign: "left" }}
            >
              Target End Effector Position: x{"  "}
              <input
                style={{ width: 50 }}
                value={targetEndEffectorPos.x.toFixed(2)}
                onChange={(e) => {
                  setTargetEndEffectorPos(
                    new Vector3(
                      parseFloat(e.target.value),
                      targetEndEffectorPos.y,
                      targetEndEffectorPos.z
                    )
                  );
                }}
                step={0.1}
                type="number"
              />
              {"  "}y{"  "}
              <input
                style={{ width: 50 }}
                value={targetEndEffectorPos.y.toFixed(2)}
                onChange={(e) => {
                  setTargetEndEffectorPos(
                    new Vector3(
                      targetEndEffectorPos.x,
                      parseFloat(e.target.value),
                      targetEndEffectorPos.z
                    )
                  );
                }}
                step={0.1}
                type="number"
              />
              {type !== "threeLinkPlanarArm" && (
                <>
                  {"  "}y{"  "}
                  <input
                    style={{ width: 50 }}
                    value={targetEndEffectorPos.z.toFixed(2)}
                    onChange={(e) => {
                      setTargetEndEffectorPos(
                        new Vector3(
                          targetEndEffectorPos.x,
                          targetEndEffectorPos.y,
                          parseFloat(e.target.value)
                        )
                      );
                    }}
                    step={0.1}
                    type="number"
                  />
                </>
              )}
            </Typography>
            <Button
              variant="contained"
              sx={{ margin: 1 }}
              onClick={() => {
                let a3 = linkList[2].a;
                let x =
                  targetEndEffectorPos.x -
                  (a3 * targetEndEffectorPos.x) /
                    Math.sqrt(
                      targetEndEffectorPos.x ** 2 + targetEndEffectorPos.y ** 2
                    );
                let y =
                  targetEndEffectorPos.y -
                  (a3 * targetEndEffectorPos.y) /
                    Math.sqrt(
                      targetEndEffectorPos.x ** 2 + targetEndEffectorPos.y ** 2
                    );
                let a1 = linkList[0].a;
                let a2 = linkList[1].a;
                let c2 = (x ** 2 + y ** 2 - a1 ** 2 - a2 ** 2) / (2 * a1 * a2);
                if (c2 > 1 || c2 < -1 || isNaN(c2)) setNoSolution(true);
                else {
                  setNoSolution(false);
                  let s2 = Math.sqrt(1 - c2 ** 2);
                  let theta2_1 = Math.atan2(s2, c2);
                  let theta2_2 = Math.atan2(-s2, c2);

                  let s1_1 =
                    ((a1 + a2 * c2) * y - a2 * s2 * x) / (x ** 2 + y ** 2);
                  let c1_1 =
                    ((a1 + a2 * c2) * x + a2 * s2 * y) / (x ** 2 + y ** 2);
                  let theta1_1 = Math.atan2(s1_1, c1_1);
                  let s1_2 =
                    ((a1 + a2 * c2) * y - a2 * -s2 * x) / (x ** 2 + y ** 2);
                  let c1_2 =
                    ((a1 + a2 * c2) * x + a2 * -s2 * y) / (x ** 2 + y ** 2);
                  let theta1_2 = Math.atan2(s1_2, c1_2);
                  let theta3_1 =
                    Math.atan2(targetEndEffectorPos.y, targetEndEffectorPos.x) -
                    theta1_1 -
                    theta2_1;
                  let theta3_2 =
                    Math.atan2(targetEndEffectorPos.y, targetEndEffectorPos.x) -
                    theta1_2 -
                    theta2_2;
                  if (isNaN(theta1_1) || isNaN(theta2_1) || isNaN(theta3_1))
                    setNoSolution(true);
                  else {
                    setResults([
                      [
                        (theta1_1 * 180) / Math.PI,
                        (theta2_1 * 180) / Math.PI,
                        (theta3_1 * 180) / Math.PI,
                      ],
                      [
                        (theta1_2 * 180) / Math.PI,
                        (theta2_2 * 180) / Math.PI,
                        (theta3_2 * 180) / Math.PI,
                      ],
                    ]);
                    const newLinkList = [...linkList];
                    newLinkList[0].theta = (theta1_1 * 180) / Math.PI;
                    newLinkList[1].theta = (theta2_1 * 180) / Math.PI;
                    newLinkList[2].theta = (theta3_1 * 180) / Math.PI;
                    setLinkList(newLinkList);
                    setResultIndex(0);
                  }
                }
              }}
            >
              Calculate IK Result
            </Button>
            {noSolution ? (
              <Typography style={{ marginTop: 20, marginBottom: 20 }}>
                No Solution
              </Typography>
            ) : (
              <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
                <InputLabel id="demo-simple-select-standard-label">
                  Configurations of IK Result
                </InputLabel>
                <Select
                  labelId="demo-simple-select-standard-label"
                  id="demo-simple-select-standard"
                  value={resultIndex}
                  onChange={handleResultIndexChange}
                  label="Age"
                >
                  {results.map((r, i) => (
                    <MenuItem value={i} key={i}>
                      <Latex>{`$\\theta_1$: ${r[0].toFixed(
                        2
                      )}, $\\theta_2$: ${r[1].toFixed(
                        2
                      )}, $\\theta_3$: ${r[2].toFixed(2)}`}</Latex>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </>
        ) : (
          <Typography sx={{ marginTop: 2 }}>
            ToDo. There are lots of feature need to do. If you really want it,
            tell me in <a href={"https://discord.gg/TfwZ3hH2D2"}>Discord</a>. I
            will do this feature first.
          </Typography>
        )}
        <NextAction
          label={"Next: 2D Jacobian Inverse Kinematics"}
          to="/2d-jacobian-ik"
        />
      </div>
    </div>
  );
}
